import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

interface Campaign {
  id: string;
  campaign_name: string;
  campaign_url: string;
  client_name: string;
  end_date: string;
  banner_image_url?: string;
}

export function BannerDisplay() {
  const [activeCampaigns, setActiveCampaigns] = useState<Campaign[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchActiveCampaigns = async () => {
    try {
      // First expire old campaigns
      await supabase.rpc('expire_old_campaigns');
      
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, campaign_name, campaign_url, client_name, end_date, banner_image_url')
        .eq('is_active', true)
        .gt('end_date', new Date().toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;
      setActiveCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching active campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveCampaigns();
    
    // Refresh campaigns every minute to check for expired ones
    const interval = setInterval(fetchActiveCampaigns, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeCampaigns.length > 1) {
      // Rotate banners every 10 seconds for even distribution
      const rotationInterval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % activeCampaigns.length);
      }, 10000);

      return () => clearInterval(rotationInterval);
    }
  }, [activeCampaigns.length]);

  if (loading) {
    return (
      <div className="w-full h-[90px] bg-muted animate-pulse rounded-md flex items-center justify-center">
        <span className="text-foreground text-sm">Loading banner...</span>
      </div>
    );
  }

  // Show permanent "Advertise with Us" banner if less than 4 active campaigns
  const showAdvertiseWithUs = activeCampaigns.length < 4;
  
  // If no active campaigns, only show the "Advertise with Us" banner
  if (activeCampaigns.length === 0) {
    return (
      <Link 
        to="/corporate" 
        className="block w-full h-[90px] bg-gradient-to-r from-primary to-primary/80 rounded-md flex items-center justify-center text-white hover:from-primary/90 hover:to-primary/70 transition-colors"
      >
        <div className="text-center">
          <h3 className="text-lg font-semibold">Advertise with Us Here</h3>
          <p className="text-sm opacity-90">Click to get started</p>
        </div>
      </Link>
    );
  }

  // Show current campaign or "Advertise with Us" based on rotation
  const shouldShowAdvertise = showAdvertiseWithUs && currentIndex >= activeCampaigns.length;
  
  if (shouldShowAdvertise) {
    return (
      <Link 
        to="/corporate" 
        className="block w-full h-[90px] bg-gradient-to-r from-primary to-primary/80 rounded-md flex items-center justify-center text-white hover:from-primary/90 hover:to-primary/70 transition-colors"
      >
        <div className="text-center">
          <h3 className="text-lg font-semibold">Advertise with Us Here</h3>
          <p className="text-sm opacity-90">Click to get started</p>
        </div>
      </Link>
    );
  }

  const currentCampaign = activeCampaigns[currentIndex % activeCampaigns.length];
  
  return (
    <a
      href={currentCampaign.campaign_url}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full h-[90px] rounded-md overflow-hidden hover:opacity-90 transition-opacity"
    >
      {currentCampaign.banner_image_url ? (
        <div className="relative w-full h-full">
          <img
            src={currentCampaign.banner_image_url}
            alt={currentCampaign.campaign_name}
            className="w-full h-full object-cover"
          />
          {activeCampaigns.length > 1 && (
            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1">
              {activeCampaigns.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full ${
                    index === currentIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-full bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-center text-white">
          <div className="text-center px-4">
            <h3 className="text-lg font-semibold truncate">{currentCampaign.campaign_name}</h3>
            <p className="text-sm opacity-90">by {currentCampaign.client_name}</p>
            {activeCampaigns.length > 1 && (
              <div className="flex justify-center mt-1 space-x-1">
                {activeCampaigns.map((_, index) => (
                  <div
                    key={index}
                    className={`w-1.5 h-1.5 rounded-full ${
                      index === currentIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </a>
  );
}